import { useState, useEffect, useCallback } from "react";
import type {
  PreviousEvaluationResult,
  CurrentEvaluation,
} from "@services/evaluationService";
import styles from "./EvaluationCard.module.css";

import overrideIcon from "@assets/icons/edit-icon.svg";
import { ATTEMPT_STATUS, EVALUATION_RESULT_LABELS, EVALUATION_RESULT_OPTIONS, type EvaluationResult, type AttemptStatus, ATTEMPT_STATUS_OPTIONS } from "@constants/evaluationConstants";

const OverrideIcon = () => (
  <img src={overrideIcon} alt="Override" width={16} height={16} />
);

export interface SMEFormData {
  marks: string;
  remarks: string;
  attemptStatus?:AttemptStatus;
}

interface SMEEvaluationFormProps {
  previousResult: PreviousEvaluationResult;
  currentEvaluation?: CurrentEvaluation | null;
  minMarks: number;
  maxMarks: number;
  isOverrideMode?: boolean;
  onCancel: () => void;
  onOverride?: () => void;
  onChange: (data: SMEFormData, isValid: boolean) => void;
}

export default function SMEEvaluationForm({
  previousResult,
  currentEvaluation,
  minMarks,
  maxMarks,
  isOverrideMode = true,
  onCancel,
  onOverride,
  onChange,
}: SMEEvaluationFormProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // If currentEvaluation exists and we're not in override mode, pre-fill with it
  const initialMarks =
    currentEvaluation && !isOverrideMode
      ? String(currentEvaluation.awarded_marks)
      : "";
  const initialRemarks =
    currentEvaluation && !isOverrideMode
      ? currentEvaluation.feedbacks.join(" ") || ""
      : "";

  const [marks, setMarks] = useState(initialMarks);
  const [remarks, setRemarks] = useState(initialRemarks);
  const [marksError, setMarksError] = useState<string | null>(null);
  const [remarksError, setRemarksError] = useState<string | null>(null);
  const [touched, setTouched] = useState({ marks: false, remarks: false });
  const [attemptStatus, setAttemptStatus] = useState<AttemptStatus>(previousResult?.attempt_status || ATTEMPT_STATUS.UNATTEMPTED);
  const isUnattempted=attemptStatus===ATTEMPT_STATUS.UNATTEMPTED;

  // Reset form when currentEvaluation changes or override mode changes
  useEffect(() => {
    if (currentEvaluation && !isOverrideMode) {
      setMarks(String(currentEvaluation.awarded_marks));
      setRemarks(currentEvaluation.feedbacks.join(" ") || "");
      setAttemptStatus(currentEvaluation?.attempt_status);
      setTouched({ marks: false, remarks: false });
      setMarksError(null);
      setRemarksError(null);
    } else if (isOverrideMode && currentEvaluation) {
      // When entering override mode, keep the current values as starting point
      setMarks(String(currentEvaluation.awarded_marks));
      setRemarks(currentEvaluation.feedbacks.join(" ") || "");
      setAttemptStatus(currentEvaluation?.attempt_status);
      setTouched({ marks: false, remarks: false });
    }
  }, [currentEvaluation, isOverrideMode]);

  const isReadOnly = !!(currentEvaluation && !isOverrideMode);

  const validateMarks = useCallback(
    (value: string): string | null => {
      if (!value.trim()) {
        return "Marks is required";
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return "Please enter a valid number";
      }
      if (numValue < minMarks) {
        return `Marks must be at least ${minMarks}`;
      }
      if (numValue > maxMarks) {
        return `Marks must not exceed ${maxMarks}`;
      }
      return null;
    },
    [minMarks, maxMarks]
  );

  const validateRemarks = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return "Remarks is required";
    }
    if (value.trim().length < 2) {
      return "Remarks must be at least 2 characters";
    }
    return null;
  }, []);

  useEffect(() => {
    // If read-only, don't validate - it's already valid
    if (isReadOnly) {
      return;
    }

    const marksValidation = validateMarks(marks);
    const remarksValidation = validateRemarks(remarks);

    if (touched.marks) {
      setMarksError(marksValidation);
    }
    if (touched.remarks) {
      setRemarksError(remarksValidation);
    }

    if(isUnattempted){
      onChange({ marks: "", remarks: "", attemptStatus: attemptStatus! }, true);
      return;
    }
    const isValid = !marksValidation && !remarksValidation;
    onChange({ marks, remarks, attemptStatus: attemptStatus! }, isValid);
  }, [
    marks,
    remarks,
    attemptStatus,
    touched,
    validateMarks,
    validateRemarks,
    onChange,
    isUnattempted,
    isReadOnly,
  ]);

  const handleMarksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    const value = e.target.value;
    // Allow empty, numbers, and decimals
    if (value === "" || /^-?\d*\.?\d*$/.test(value)) {
      setMarks(value);
    }
  };

  const handleMarksBlur = () => {
    if (isReadOnly) return;
    setTouched((prev) => ({ ...prev, marks: true }));
  };

  const handleRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isReadOnly) return;
    setRemarks(e.target.value);
  };

  const handleRemarksBlur = () => {
    if (isReadOnly) return;
    setTouched((prev) => ({ ...prev, remarks: true }));
  };

  const handleAttempStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as AttemptStatus;
    setAttemptStatus(value);
    if(value===ATTEMPT_STATUS.UNATTEMPTED){
      setMarks("");
      setRemarks("");
      setTouched((prev) => ({ ...prev, marks: false, remarks: false }));
    }
  }

  const EvaluationResultBadge = ({ evaluation_result }: { evaluation_result: EvaluationResult }) => {
  
    return (
      <span 
        className={styles.badgeGreyed}
      >
        {EVALUATION_RESULT_LABELS[evaluation_result]}
      </span>
    );
  };

  const UnattemptedBadge = () => {
    return (
      <span 
        className={styles.badgeGreyed}
      >
        {ATTEMPT_STATUS.UNATTEMPTED}
      </span>
    );
  }

  const feedbackText = previousResult.feedbacks.join(" ");
  const shouldShowReadMore = feedbackText.length > 60;

  return (
    <div className={styles.contentSection}>
      {/* L1 Evaluation Summary (Grey/Disabled) */}
      <div className={styles.l1CardGrey}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitleGrey}>L1 Evaluation</span>
          {previousResult?.attempt_status !== ATTEMPT_STATUS.UNATTEMPTED ? previousResult?.evaluation_result && <EvaluationResultBadge evaluation_result={previousResult?.evaluation_result} />: <UnattemptedBadge />}
        </div>
        <span className={styles.score}>
          {previousResult.awarded_marks} / {maxMarks}
        </span>
        <div className={styles.feedbackContainer}>
          <span
            className={
              isExpanded || !shouldShowReadMore
                ? styles.feedbackText
                : styles.feedbackTextTruncated
            }
          >
            {feedbackText}
          </span>
          {shouldShowReadMore && (
            <button
              className={styles.readMore}
              onClick={() => setIsExpanded(!isExpanded)}
              type="button"
            >
              {isExpanded ? "read less" : "read more"}
            </button>
          )}
        </div>
      </div>

      {/* SME Evaluation Form */}
      <div className={styles.smeCard}>
        <div className={styles.smeHeader}>
          <span className={styles.smeTitle}>SME Evaluation</span>
          {isReadOnly ? (
            <button
              className={styles.overrideButton}
              onClick={onOverride}
              type="button"
            >
              <OverrideIcon />
              <span className={styles.overrideButtonText}>Override</span>
            </button>
          ) : (
            <button
              className={styles.cancelButton}
              onClick={onCancel}
              type="button"
            >
              <span className={styles.cancelButtonText}>Cancel</span>
            </button>
          )}
        </div>

        <div className={styles.formContainer}>
          <div className={styles.marksSection}>
            <div className={styles.marksInputRow}>
              <input
                type="text"
                className={
                  marksError && touched.marks
                    ? styles.marksInputError
                    : isReadOnly
                    ? styles.marksInputReadOnly
                    : styles.marksInput
                }
                value={marks}
                onChange={handleMarksChange}
                onBlur={handleMarksBlur}
                placeholder="0"
                aria-label="Marks"
                readOnly={isReadOnly}
              />
              <span className={styles.maxMarks}>/ {maxMarks}</span>
            </div>
            {marksError && touched.marks ? (
              <span className={styles.errorText}>{marksError}</span>
            ) : (
              <span className={styles.helperText}>
                {isReadOnly
                  ? "Click Override to modify"
                  : "Enter marks based on the rubric criteria"}
              </span>
            )}
          </div>
          <div className={styles.attempStatusStyle}>
          <label>Evaluation Status: </label>
          <select value={attemptStatus} onChange={handleAttempStatusChange}>
            {ATTEMPT_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
          {!isUnattempted && <div className={styles.remarksSection}>
            <label className={styles.remarksLabel}>Remarks</label>
            <textarea
              className={
                remarksError && touched.remarks
                  ? styles.remarksTextareaError
                  : isReadOnly
                  ? styles.remarksTextareaReadOnly
                  : styles.remarksTextarea
              }
              value={remarks}
              onChange={handleRemarksChange}
              onBlur={handleRemarksBlur}
              placeholder="Type remarks here"
              aria-label="Remarks"
              readOnly={isReadOnly}
            />
            {remarksError && touched.remarks && (
              <span className={styles.errorText}>{remarksError}</span>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
}
